import type { ExpenseInvoiceSuggestionForBookingResponseItem } from './expenseInvoicesLookupApi.contract';

export interface BookingExpenseInvoiceLookup {
  id: number;
  documentKind: string | null;
  documentReference: string;
  displayIssuerName: string;
  description: string | null;
  tags: BookingExpenseInvoiceTagLookup[];
  invoiceNumber: string;
  supplierName: string;
  currency: string;
  totalGross: number;
}

export interface BookingExpenseInvoiceTagLookup {
  id: number;
  name: string;
}

export interface BookingExpenseInvoiceAssignmentLookup {
  bookingId: number;
  allocatedAmount: number;
  assignmentTagId?: number | null;
  assignmentTagName?: string | null;
}

export interface SaveBookingExpenseInvoiceAssignmentLookup {
  bookingId: number;
  allocatedAmount: number;
  tagId?: number | null;
}

export interface BookingExpenseInvoiceDetailsLookup {
  id: number;
  documentKind: string | null;
  documentReference: string;
  displayIssuerName: string;
  description: string | null;
  tags: BookingExpenseInvoiceTagLookup[];
  currency: string;
  totalGross: number;
  bookings: BookingExpenseInvoiceAssignmentLookup[];
}

export interface SaveBookingExpenseInvoiceAssignmentsRequest {
  invoiceId: number;
  assignments: SaveBookingExpenseInvoiceAssignmentLookup[];
}

interface ExpenseInvoiceListLookupShape {
  id: number;
  documentKind?: string | null;
  invoiceNumber: string | null;
  supplierName: string | null;
  documentReference?: string | null;
  displayIssuerName?: string | null;
  description?: string | null;
  tags?: ExpenseInvoiceTagLookupShape[] | null;
  currency?: string | null;
  totalGross: number;
}

interface ExpenseInvoiceTagLookupShape {
  id: number;
  name: string | null;
}

interface ExpenseInvoiceDisplayLookupShape {
  id: number;
  invoiceNumber?: string | null;
  supplierName?: string | null;
  documentReference?: string | null;
  displayIssuerName?: string | null;
}

interface ExpenseInvoiceDetailsBookingShape {
  id: number;
  allocatedAmount: number;
  assignmentTagId?: number | null;
  assignmentTagName?: string | null;
  tagId?: number | null;
  tagName?: string | null;
}

interface ExpenseInvoiceDetailsLookupShape {
  id: number;
  documentKind?: string | null;
  invoiceNumber?: string | null;
  supplierName?: string | null;
  documentReference?: string | null;
  displayIssuerName?: string | null;
  description?: string | null;
  tags?: ExpenseInvoiceTagLookupShape[] | null;
  currency?: string | null;
  totalGross: number;
  bookings?: ExpenseInvoiceDetailsBookingShape[] | null;
}

const normalizeString = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : null;
};

const mapExpenseInvoiceTagsToBookingExpenseInvoiceTags = (
  tags: ExpenseInvoiceTagLookupShape[] | null | undefined
): BookingExpenseInvoiceTagLookup[] =>
  (tags ?? [])
    .map((tag) => {
      const normalizedName = normalizeString(tag?.name);

      if (typeof tag?.id !== 'number' || !Number.isFinite(tag.id) || !normalizedName) {
        return null;
      }

      return {
        id: tag.id,
        name: normalizedName,
      };
    })
    .filter((tag): tag is BookingExpenseInvoiceTagLookup => tag !== null);

const resolveBookingExpenseInvoiceDocumentReference = (
  expenseInvoice: ExpenseInvoiceDisplayLookupShape
): string =>
  normalizeString(expenseInvoice.documentReference) ??
  normalizeString(expenseInvoice.invoiceNumber) ??
  `#${expenseInvoice.id}`;

const resolveBookingExpenseInvoiceDisplayIssuerName = (
  expenseInvoice: ExpenseInvoiceDisplayLookupShape
): string =>
  normalizeString(expenseInvoice.displayIssuerName) ??
  normalizeString(expenseInvoice.supplierName) ??
  'Koszt uproszczony';

const resolveAssignmentTagId = (
  booking: ExpenseInvoiceDetailsBookingShape
): number | null | undefined => {
  const hasAssignmentTagId = Object.prototype.hasOwnProperty.call(booking, 'assignmentTagId');
  const hasLegacyTagId = Object.prototype.hasOwnProperty.call(booking, 'tagId');

  if (!hasAssignmentTagId && !hasLegacyTagId) {
    return undefined;
  }

  const rawTagId = hasAssignmentTagId ? booking.assignmentTagId : booking.tagId;
  return typeof rawTagId === 'number' && Number.isFinite(rawTagId) && rawTagId > 0 ? rawTagId : null;
};

const resolveAssignmentTagName = (
  booking: ExpenseInvoiceDetailsBookingShape
): string | null | undefined => {
  const hasAssignmentTagName = Object.prototype.hasOwnProperty.call(booking, 'assignmentTagName');
  const hasLegacyTagName = Object.prototype.hasOwnProperty.call(booking, 'tagName');

  if (!hasAssignmentTagName && !hasLegacyTagName) {
    return undefined;
  }

  const rawTagName = hasAssignmentTagName ? booking.assignmentTagName : booking.tagName;
  return normalizeString(rawTagName);
};

export const mapExpenseInvoiceToBookingExpenseInvoiceLookup = (
  expenseInvoice: ExpenseInvoiceListLookupShape | undefined | null
): BookingExpenseInvoiceLookup | undefined => {
  if (!expenseInvoice) {
    return undefined;
  }

  return {
    id: expenseInvoice.id,
    documentKind: normalizeString(expenseInvoice.documentKind),
    documentReference: resolveBookingExpenseInvoiceDocumentReference(expenseInvoice),
    displayIssuerName: resolveBookingExpenseInvoiceDisplayIssuerName(expenseInvoice),
    description: normalizeString(expenseInvoice.description),
    tags: mapExpenseInvoiceTagsToBookingExpenseInvoiceTags(expenseInvoice.tags),
    invoiceNumber:
      normalizeString(expenseInvoice.invoiceNumber) ??
      resolveBookingExpenseInvoiceDocumentReference(expenseInvoice),
    supplierName:
      normalizeString(expenseInvoice.supplierName) ??
      resolveBookingExpenseInvoiceDisplayIssuerName(expenseInvoice),
    currency: expenseInvoice.currency ?? 'PLN',
    totalGross: expenseInvoice.totalGross,
  };
};

export const mapExpenseInvoiceDetailsToBookingExpenseInvoiceDetailsLookup = (
  expenseInvoice: ExpenseInvoiceDetailsLookupShape | undefined | null
): BookingExpenseInvoiceDetailsLookup | undefined => {
  if (!expenseInvoice) {
    return undefined;
  }

  return {
    id: expenseInvoice.id,
    documentKind: normalizeString(expenseInvoice.documentKind),
    documentReference: resolveBookingExpenseInvoiceDocumentReference(expenseInvoice),
    displayIssuerName: resolveBookingExpenseInvoiceDisplayIssuerName(expenseInvoice),
    description: normalizeString(expenseInvoice.description),
    tags: mapExpenseInvoiceTagsToBookingExpenseInvoiceTags(expenseInvoice.tags),
    currency: expenseInvoice.currency ?? 'PLN',
    totalGross: expenseInvoice.totalGross,
    bookings: (expenseInvoice.bookings ?? []).map((booking) => {
      const assignmentTagId = resolveAssignmentTagId(booking);
      const assignmentTagName = resolveAssignmentTagName(booking);

      return {
        bookingId: booking.id,
        allocatedAmount: booking.allocatedAmount,
        ...(assignmentTagId !== undefined ? { assignmentTagId } : {}),
        ...(assignmentTagName !== undefined ? { assignmentTagName } : {}),
      };
    }),
  };
};

export const buildSaveBookingExpenseInvoiceAssignmentsRequest = (
  request: SaveBookingExpenseInvoiceAssignmentsRequest
): SaveBookingExpenseInvoiceAssignmentsRequest => request;

/**
 * Confidence tier for a completely-unassigned expense invoice suggested for this booking,
 * mirrored locally from the backend `BookingSuggestionConfidence` enum (camelCase JSON).
 */
export const bookingExpenseInvoiceSuggestionConfidenceLevels = ['high', 'medium', 'low'] as const;

export type BookingExpenseInvoiceSuggestionConfidence =
  (typeof bookingExpenseInvoiceSuggestionConfidenceLevels)[number];

export interface BookingExpenseInvoiceSuggestionLookup {
  id: number;
  documentReference: string;
  displayIssuerName: string;
  issueDate: string;
  saleDate: string | null;
  totalGross: number;
  currency: string;
  score: number;
  confidence: BookingExpenseInvoiceSuggestionConfidence;
  reasons: string[];
}

const isValidIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const isValidBookingExpenseInvoiceSuggestionConfidence = (
  value: unknown
): value is BookingExpenseInvoiceSuggestionConfidence =>
  typeof value === 'string' &&
  (bookingExpenseInvoiceSuggestionConfidenceLevels as readonly string[]).includes(value);

/**
 * Maps a single raw `expense-invoice-suggestions` API response item to the local
 * `BookingExpenseInvoiceSuggestionLookup` read contract. Returns `null` for a malformed
 * item instead of throwing, so one bad entry does not break the whole suggestions list.
 */
export const mapExpenseInvoiceSuggestionToBookingExpenseInvoiceSuggestionLookup = (
  suggestion: ExpenseInvoiceSuggestionForBookingResponseItem
): BookingExpenseInvoiceSuggestionLookup | null => {
  if (
    typeof suggestion?.expenseInvoiceId !== 'number' ||
    !Number.isFinite(suggestion.expenseInvoiceId) ||
    !isValidIsoDate(suggestion.issueDate) ||
    typeof suggestion.totalGross !== 'number' ||
    typeof suggestion.currency !== 'string' ||
    typeof suggestion.score !== 'number' ||
    !isValidBookingExpenseInvoiceSuggestionConfidence(suggestion.confidence)
  ) {
    return null;
  }

  return {
    id: suggestion.expenseInvoiceId,
    documentReference: normalizeString(suggestion.invoiceNumber) ?? `#${suggestion.expenseInvoiceId}`,
    displayIssuerName: normalizeString(suggestion.supplierName) ?? 'Nieznany dostawca',
    issueDate: suggestion.issueDate,
    saleDate: isValidIsoDate(suggestion.saleDate) ? suggestion.saleDate : null,
    totalGross: suggestion.totalGross,
    currency: suggestion.currency,
    score: suggestion.score,
    confidence: suggestion.confidence,
    reasons: Array.isArray(suggestion.reasons)
      ? suggestion.reasons.filter((reason): reason is string => typeof reason === 'string')
      : [],
  };
};

/**
 * Maps the raw `expense-invoice-suggestions` API response array to the local
 * `BookingExpenseInvoiceSuggestionLookup[]` read contract, dropping malformed entries.
 */
export const mapExpenseInvoiceSuggestionsForBookingResponse = (
  response: ExpenseInvoiceSuggestionForBookingResponseItem[] | null | undefined
): BookingExpenseInvoiceSuggestionLookup[] =>
  (response ?? [])
    .map((suggestion) => mapExpenseInvoiceSuggestionToBookingExpenseInvoiceSuggestionLookup(suggestion))
    .filter((suggestion): suggestion is BookingExpenseInvoiceSuggestionLookup => suggestion !== null);
